import { memo, useMemo } from "react";
import { CellContext } from "@tanstack/react-table";
import { CaptureClicks } from "@design-system/CaptureClicks/CaptureClicks";
import type { ViewTableFeatures } from "@design-system/ViewTable/core/table";
import { EditableCellContent } from "@design-system/ViewTable/EditableCellContent";
import { ViewTableCell } from "@design-system/ViewTable/ViewTableCell";
import { CloseOverviewMember } from "@common/schemas/clientsCloseOverview.schema";
import { CloseOverviewMemberList } from "@components/organizationView/clients-closes/cells/CloseOverviewMemberList";
import {
    buildRolesByUserId,
    deduplicateMembers,
} from "@components/organizationView/clients-closes/cells/closeOverviewMemberHelpers";
import {
    ClientsWorkOverviewRow,
    ClientsWorkOverviewWorkRow,
} from "../clientsWorkOverviewViewConfig";
import { useWorkEditability } from "../editing/workItemEditing";
import { WorkTeamPopover } from "../editing/WorkRolePicker";
import { EMPTY_CELL_VALUE } from "./emptyCellValue";

/** Whether an application has anyone on its engagement team. */
export const hasWorkEngagementTeam = ({
    preparers,
    reviewers,
    managers,
}: Pick<ClientsWorkOverviewWorkRow, "preparers" | "reviewers" | "managers">) =>
    preparers.length > 0 || reviewers.length > 0 || managers.length > 0;

/** An application's preparers, reviewers and managers, each once, with their roles. */
export const WorkEngagementTeam = ({
    row,
}: {
    row: Pick<
        ClientsWorkOverviewWorkRow,
        "preparers" | "reviewers" | "managers"
    >;
}) => {
    const rolesByUserId = useMemo(
        () => buildRolesByUserId(row.preparers, row.reviewers, row.managers),
        [row.preparers, row.reviewers, row.managers],
    );

    return (
        <CloseOverviewMemberList
            members={deduplicateMembers([
                ...row.preparers,
                ...row.reviewers,
                ...row.managers,
            ])}
            rolesByUserId={rolesByUserId}
        />
    );
};

export const WorkPreparerReviewerCell = memo(
    ({
        row,
    }: CellContext<
        ViewTableFeatures,
        ClientsWorkOverviewRow,
        CloseOverviewMember[]
    >) => {
        const { isTeamEditable } = useWorkEditability();
        const original = row.original;
        if (original.rowType !== "work") {
            return EMPTY_CELL_VALUE;
        }
        const hasTeam = hasWorkEngagementTeam(original);
        if (!isTeamEditable(original)) {
            return hasTeam ? (
                <WorkEngagementTeam row={original} />
            ) : (
                EMPTY_CELL_VALUE
            );
        }

        return (
            <ViewTableCell>
                <div>
                    <CaptureClicks>
                        <WorkTeamPopover
                            row={original}
                            trigger={
                                <EditableCellContent
                                    isEmpty={!hasTeam}
                                    addText="Assign team"
                                    hideIcon
                                >
                                    <WorkEngagementTeam row={original} />
                                </EditableCellContent>
                            }
                        />
                    </CaptureClicks>
                </div>
            </ViewTableCell>
        );
    },
);

WorkPreparerReviewerCell.displayName = "WorkEngagementTeamCell";
